import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { BaseRequester } from 'src/app/shared/services/common/base-requester';
import { environment } from 'src/environments/environment';
import { map } from 'rxjs/operators';
import { AjaxRequest, AjaxResponse, ajax } from 'rxjs/ajax';
import { get as _get, toString as _toString, trim as _trim } from 'lodash';

@Injectable({
    providedIn: 'root',
})
export class CounterpartyService extends BaseRequester {
    // 100 is the scraper's cap on records per request, and it offers no
    // pagination — anything beyond this is simply not retrievable. It matters
    // most for a tax-ID lookup, where the default of 5 would silently truncate
    // a company's branch list.
    private static readonly SCRAPER_MAX_RESULTS = 100;

    // A pasted tax ID often carries surrounding whitespace (and sometimes the
    // separators people write it with). Cleaning it here rather than at the
    // call sites means no caller can send one the backends will fail to match.
    static toTaxId(taxId: string): string {
        return _toString(taxId).replace(/\D+/g, '');
    }

    getDbdCounterpartyDataAlt(taxId: string): Observable<any> {
        const ajaxRequest: AjaxRequest = {
            // toTaxId leaves digits only, so there is nothing left to encode.
            url: `https://openapi.dbd.go.th/api/v1/juristic_person/${CounterpartyService.toTaxId(
                taxId,
            )}`,
            method: 'GET',
            responseType: 'json',
        };
        return ajax(ajaxRequest).pipe(
            map((res: AjaxResponse) => {
                if (res.status === 200) {
                    if (_get(res, 'response.status.code') === '1000') {
                        const dataList: any[] = _get(res, 'response.data', []);
                        return dataList;
                    } else if (_get(res, 'response.status.code') === '1004') {
                        return [];
                    }
                    return throwError(`Dbd service error: ${res.responseText}`);
                }
                return throwError(res.responseText);
            }),
        );
    }

    // A company and its branches. maxResults must be sent explicitly or the
    // scraper returns its default of 5, silently truncating the branch list.
    getDataForThaiCompanyData(taxId: string): Observable<any> {
        return this.scrape('dataforthai', {
            taxId: CounterpartyService.toTaxId(taxId),
            maxResults: CounterpartyService.SCRAPER_MAX_RESULTS,
        });
    }

    // Searches companies by name. Returns companies, not branches — the records
    // carry only taxId and name, so feed a chosen taxId into
    // getDataForThaiCompanyData to get that company's address and branches.
    // Capped well below the branch limit: the list is rendered one row per
    // match with no paging, and the pick is followed by a tax-ID lookup anyway.
    private static readonly NAME_SEARCH_MAX_RESULTS = 25;

    searchCompaniesByName(companyName: string): Observable<any> {
        return this.scrape('business-search', {
            // The API requires a non-empty name after trimming.
            companyName: _trim(_toString(companyName)),
            maxResults: CounterpartyService.NAME_SEARCH_MAX_RESULTS,
        });
    }

    private scrape(scraper: string, reqData: object): Observable<any> {
        return this.internalPostRequest(environment.scraperApiEndpoint, {
            scraper,
            reqData,
        }).pipe(map((res: AjaxResponse) => _get(res, 'response')));
    }
}
